import { getReframeCropRegion } from "../containers/shorts/shorts-helper";

// ─────────────────────────────────────────────────────────────────────────────
// Thumbnail Generation — Optimized
//
// Key improvements over the original:
//   1. ONE shared <video> for ALL thumbnail extractions (not one per card)
//   2. LRU cache — same (src, time) pair is never extracted twice
//   3. Serial queue — extractions run one-at-a-time; no browser decode spikes
//   4. Callers get the cached result instantly on repeat calls
// ─────────────────────────────────────────────────────────────────────────────

const CACHE_MAX = 200; // max frames to keep in memory
const thumbnailCache = new Map(); // key → objectURL

/** Evict oldest entry when cache is full */
const evictIfNeeded = () => {
  if (thumbnailCache.size >= CACHE_MAX) {
    const firstKey = thumbnailCache.keys().next().value;
    const url = thumbnailCache.get(firstKey);
    URL.revokeObjectURL(url);
    thumbnailCache.delete(firstKey);
  }
};

// Shared extraction video — created once, reused for every card
let extractorVideo = null;
let extractorReady = false; // true once loadedmetadata fired for current src
let extractorSrc = null;

// Serial promise queue — only one extraction runs at a time
let extractionQueue = Promise.resolve();

const getExtractorVideo = () => {
  if (!extractorVideo) {
    extractorVideo = document.createElement("video");
    extractorVideo.crossOrigin = "anonymous";
    extractorVideo.preload = "auto";
    extractorVideo.muted = true;
    extractorVideo.playsInline = true;
  }
  return extractorVideo;
};

/**
 * Eagerly start loading/buffering the video so seeks are instant by the time
 * individual cards request frame extraction.
 */
export const preloadExtractorVideo = (videoSrc) => {
  if (!videoSrc) return;
  const video = getExtractorVideo();
  if (extractorSrc === videoSrc) return; // already loading this src
  extractorSrc = videoSrc;
  extractorReady = false;
  video.src = videoSrc;
  video.addEventListener(
    "loadedmetadata",
    () => { extractorReady = true; },
    { once: true }
  );
  video.load();
};

/**
 * Draw the current video frame onto a canvas cropped to the target aspect ratio.
 * Uses reframe focus point when provided (same logic as shorts player).
 */
const captureFrame = (video, reframe, aspectRatio = 9 / 16) =>
  new Promise((resolve) => {
    const CANVAS_W = 180;
    const CANVAS_H = Math.round(CANVAS_W / aspectRatio);

    const { videoWidth: vw, videoHeight: vh } = video;
    const { sx, sy, sw, sh } = getReframeCropRegion(vw, vh, aspectRatio, reframe);

    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, CANVAS_W, CANVAS_H);

    canvas.toBlob(
      (blob) => resolve(blob ? URL.createObjectURL(blob) : null),
      "image/webp",
      0.75
    );
  });

/**
 * Seek the shared extractor video to `timeInSeconds` and capture the frame.
 * Returns a promise that resolves to a blob: URL or null.
 */
const extractFrame = (videoSrc, timeInSeconds, options = {}) =>
  new Promise((resolve) => {
    const video = getExtractorVideo();
    const { reframe, aspectRatio } = options;

    const doSeek = () => {
      if (!Number.isFinite(timeInSeconds)) {
        resolve(null);
        return;
      }
      const onSeeked = async () => {
        const url = await captureFrame(video, reframe, aspectRatio);
        resolve(url);
      };
      video.addEventListener("seeked", onSeeked, { once: true });

      // fastSeek is less accurate but much faster where available (Firefox)
      if (video.fastSeek) {
        video.fastSeek(timeInSeconds);
      } else {
        video.currentTime = timeInSeconds;
      }
    };

    if (extractorSrc === videoSrc && extractorReady) {
      doSeek();
    } else {
      extractorSrc = videoSrc;
      extractorReady = false;
      video.src = videoSrc;
      video.addEventListener(
        "loadedmetadata",
        () => {
          extractorReady = true;
          doSeek();
        },
        { once: true }
      );
      video.addEventListener("error", () => resolve(null), { once: true });
      video.load();
    }
  });

/**
 * Public API — drop-in replacement for the original thumbnailGeneration().
 *
 * • Returns cached result synchronously-wrapped in a promise when available.
 * • Queues new extractions serially so the browser is never overloaded.
 */
export const thumbnailGeneration = (videoSrc, timeInSeconds, options = {}) => {
  if (!videoSrc) return Promise.resolve(null);

  const { reframe, aspectRatio = 9 / 16 } = options;
  const reframeKey = reframe
    ? `${reframe.x}_${reframe.y}`
    : "center";
  const cacheKey = `${videoSrc}@${timeInSeconds}@${aspectRatio}@${reframeKey}`;

  if (thumbnailCache.has(cacheKey)) {
    return Promise.resolve(thumbnailCache.get(cacheKey));
  }

  // Chain onto the serial queue so extractions never overlap
  const task = extractionQueue.then(async () => {
    // Double-check cache in case a parallel caller already resolved it
    if (thumbnailCache.has(cacheKey)) {
      return thumbnailCache.get(cacheKey);
    }

    const url = await extractFrame(videoSrc, timeInSeconds, { reframe, aspectRatio });

    if (url) {
      evictIfNeeded();
      thumbnailCache.set(cacheKey, url);
    }

    return url;
  });

  // Swallow errors so a bad frame never breaks the whole queue
  extractionQueue = task.catch(() => {});

  return task;
};